import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useProject } from "../../context/ProjectContext";
import AdminLayout from "../../components/admin/AdminLayout";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Skeleton } from "../../components/ui/skeleton";
import { toast } from "sonner";
import { UserPlus, CheckCircle, XCircle, ExternalLink, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminWriterApps = () => {
  const { getAuthHeaders } = useAuth();
  const { currentProject, projects } = useProject();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const params = currentProject ? `?project_id=${currentProject.id}` : "";
      const response = await axios.get(`${API}/writer-applications${params}`, { headers: getAuthHeaders() });
      setApplications(response.data);
    } catch (error) {
      console.error("Failed to fetch applications:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject]);

  const handleApprove = async (appId) => {
    setProcessingId(appId);
    try {
      await axios.post(`${API}/writer-applications/${appId}/approve`, {}, { headers: getAuthHeaders() });
      toast.success("Application approved! Writer account created.");
      await fetchApplications();
    } catch (error) {
      toast.error("Failed to approve application");
    } finally {
      setProcessingId(null);
    }
  };

  const handleDecline = async (appId) => {
    setProcessingId(appId);
    try {
      await axios.post(`${API}/writer-applications/${appId}/decline`, {}, { headers: getAuthHeaders() });
      toast.success("Application declined");
      await fetchApplications();
    } catch (error) {
      toast.error("Failed to decline application");
    } finally {
      setProcessingId(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-[#FEF3C7] text-[#92400E]",
      approved: "bg-[#DCFCE7] text-[#166534]",
      declined: "bg-[#FEE2E2] text-[#991B1B]"
    };
    return (
      <Badge className={styles[status] || styles.pending}>
        {status}
      </Badge>
    );
  };

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project?.name || "Unknown Project";
  };

  return (
    <AdminLayout title="Writer Applications">
      <div className="space-y-6" data-testid="admin-writer-apps">
        {/* Stats */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-[#64748B]">
            <UserPlus className="h-4 w-4" />
            <span>{applications.filter(a => a.status === "pending").length} pending applications</span>
          </div>
        </div>

        {/* Applications List */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <Card key={i} className="border-0 shadow-sm">
                <CardContent className="p-6">
                  <Skeleton className="h-32 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : applications.length > 0 ? (
          <div className="space-y-4">
            {applications.map((app) => (
              <Card key={app.id} className="border-0 shadow-sm" data-testid={`application-card-${app.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <h3 className="font-semibold text-lg text-[#0F172A]">{app.name}</h3>
                      <p className="text-[#64748B]">{app.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(app.status)}
                      <Badge variant="outline">{getProjectName(app.project_id)}</Badge>
                    </div>
                  </div>
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-[#64748B] mb-1">Bio</h4>
                    <p className="text-[#0F172A]">{app.bio}</p>
                  </div>
                  {app.expertise?.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-[#64748B] mb-2">Expertise</h4>
                      <div className="flex items-center gap-2 flex-wrap">
                        {app.expertise.map((exp) => (
                          <Badge key={exp} variant="secondary">{exp}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {app.portfolio_url && (
                    <div className="mb-4">
                      <a
                        href={app.portfolio_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#3B82F6] hover:underline flex items-center gap-1"
                      >
                        <ExternalLink className="h-4 w-4" />
                        View Portfolio
                      </a>
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-4 border-t">
                    <span className="text-sm text-[#64748B]">
                      Applied: {new Date(app.created_at).toLocaleDateString()}
                    </span>
                    {app.status === "pending" && (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDecline(app.id)}
                          disabled={processingId === app.id}
                          data-testid={`decline-app-${app.id}`}
                        >
                          {processingId === app.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <XCircle className="h-4 w-4 mr-1" />
                              Decline
                            </>
                          )}
                        </Button>
                        <Button
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleApprove(app.id)}
                          disabled={processingId === app.id}
                          data-testid={`approve-app-${app.id}`}
                        >
                          {processingId === app.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Approve
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-0 shadow-sm">
            <CardContent className="py-12 text-center">
              <UserPlus className="h-12 w-12 mx-auto mb-4 text-[#64748B] opacity-50" />
              <h3 className="font-semibold text-[#0F172A] mb-2">No applications</h3>
              <p className="text-[#64748B]">Writer applications will appear here</p>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminWriterApps;
